// components/legal/SoftwareLicenseAgreement.js
import React, { useEffect } from 'react';
import styled from 'styled-components';
import Container from '../common/Container';
import { SectionTitle } from '../common/SharedStyles';
import Button from '../common/Button';
import { Link } from 'react-router-dom';

const LegalSection = styled.section`
  padding: 6rem 0;
  background-color: var(--background-primary);
`;

const LegalContainer = styled(Container)`
  max-width: 800px;
`;

const EffectiveDate = styled.p`
  font-style: italic;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  text-align: center;
`;

const SectionHeading = styled.h2`
  color: var(--accent-secondary);
  margin-top: 2.5rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const SubHeading = styled.h3`
  color: var(--text-primary);
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-size: 1.15rem;
`;

const CompanyInfo = styled.div`
  background-color: var(--background-secondary);
  padding: 1.5rem;
  border-radius: 5px;
  margin: 1.5rem 0;
  border: 1px solid var(--border-color);
`;

const BackButton = styled(Button)`
  margin-top: 3rem;
`;

const Emphasis = styled.span`
  font-weight: bold;
  color: var(--accent-primary);
`;

const SoftwareLicenseAgreement = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalSection id="software-license-agreement">
      <LegalContainer>
        <SectionTitle>Software License & Subscription Agreement</SectionTitle>
        <EffectiveDate>Effective Date: August 26th, 2026</EffectiveDate>

        <CompanyInfo>
          <p>
            Company: Signage LLC<br />
            Address: 216 Remount Dr, San Antonio, TX 78218<br />
            Contact: support@signage-llc.com
          </p>
        </CompanyInfo>

        <p>
          This Software License & Subscription Agreement ("Agreement") governs your use of the Sivo
          software ("Software") provided by Signage LLC ("Signage," "we," "our," or "us"). By
          downloading, installing, or using the Software, you ("Customer" or "you") agree to the terms of
          this Agreement.
        </p>

        <SectionHeading>1. License Grant</SectionHeading>
        <ul>
          <li>
            Signage grants you a limited, non-exclusive, non-transferable, revocable license to
            access and use the Software during the active term of your paid subscription.
          </li>
          <li>
            The Software is licensed, not sold. You receive the right to use the Software only while
            your subscription is active.
          </li>
        </ul>

        <SectionHeading>2. Subscription & Access</SectionHeading>
        <ul>
          <li>
            The Software is provided on a subscription basis. You must maintain an active paid
            subscription to access and use it.
          </li>
          <li>
            If payment is not received, or your subscription is canceled, your access to the Software
            will immediately end.
          </li>
          <li>
            Customers may invite staff or team members to access and use the Software through
            their own account, provided that such use is limited to the Customer's business
            operations.
          </li>
        </ul>

        <SectionHeading>3. Restrictions</SectionHeading>
        <p>You agree not to:</p>
        <ul>
          <li>Resell, sublicense, or share your account outside your business.</li>
          <li>Reverse engineer, decompile, or attempt to derive the source code of the Software.</li>
          <li>Use the Software in any unlawful manner or in violation of applicable laws.</li>
        </ul>

        <SectionHeading>4. Ownership</SectionHeading>
        <ul>
          <li>
            The Software, including all intellectual property rights, is and will remain the sole
            property of Signage LLC.
          </li>
          <li>This Agreement does not transfer ownership of the Software to you.</li>
          <li>
            Your subscription gives you the right to use the Software to improve your business
            efficiency, save time, and increase profitability — but it does not give you ownership
            rights.
          </li>
        </ul>

        <SectionHeading>5. Termination</SectionHeading>
        <ul>
          <li>
            You may cancel your subscription at any time, but fees are non-refundable except as
            outlined in our Terms of Service.
          </li>
          <li>Signage may suspend or terminate your access if you violate this Agreement.</li>
          <li>Upon termination, you must immediately stop using the Software.</li>
        </ul>

        <SectionHeading>6. Disclaimer of Warranties</SectionHeading>
        <ul>
          <li>The Software is provided "as is" and "as available."</li>
          <li>
            <Emphasis>
              While Signage takes pride in delivering high-quality, precision-built software
              designed to help businesses succeed, we cannot guarantee that the Software will
              always be error-free or uninterrupted.
            </Emphasis>
          </li>
          <li>
            To the maximum extent permitted by law, Signage disclaims all warranties, whether
            express or implied, including fitness for a particular purpose.
          </li>
        </ul>

        <SectionHeading>7. Limitation of Liability</SectionHeading>
        <ul>
          <li>
            To the fullest extent permitted by law, Signage is not liable for any indirect, incidental, or
            consequential damages arising out of your use of the Software.
          </li>
          <li>
            In any case, Signage's total liability will not exceed the amount you paid for your
            subscription in the last 12 months.
          </li>
        </ul>

        <SectionHeading>8. User-Provided Data, No Affiliation, and No Professional Advice Disclaimer</SectionHeading>
        <p>
          Sivo, a product of Signage LLC ("Signage," "we," "our," or "us"), provides a software
          platform that enables users to input, manage, and analyze pricing, material specifications,
          and supplier-related data for their own business purposes.
        </p>

        <SubHeading>User-Provided Data</SubHeading>
        <p>
          All pricing, cost information, material details, and related data available within the
          platform are entered and maintained solely by users. Signage does not verify, validate,
          monitor, or guarantee the accuracy, completeness, or reliability of any user-submitted
          data.
        </p>

        <SubHeading>Product Improvement &amp; Platform Development</SubHeading>
        <p>
          To improve Sivo and provide a better user experience, Signage may analyze supplier names,
          material names, product categories, and other business configuration information voluntarily
          entered into the platform. This information may be used to expand supplier and material
          databases, improve platform functionality, develop new features, and enhance the Software.
          Signage does not sell user data to third parties and does not disclose customer-specific
          pricing information to other users.
        </p>

        <SubHeading>No Affiliation with Suppliers</SubHeading>
        <p>
          Signage is not affiliated with, endorsed by, or partnered with any supplier, distributor,
          or manufacturer. The platform does not provide official pricing, product listings, or
          proprietary data from any third party. Any material names, specifications, or descriptions
          are used strictly for general identification and organizational purposes.
        </p>

        <SubHeading>Estimates &amp; Informational Use Only</SubHeading>
        <p>
          Any calculations, estimates, projections, or outputs generated by Sivo are based entirely
          on user-provided inputs and are intended for informational and internal business use only.
          These outputs do not constitute guarantees of pricing, costs, margins, or business
          outcomes.
        </p>

        <SubHeading>No Professional Advice</SubHeading>
        <p>
          Sivo and Signage do not provide financial, accounting, legal, or professional advice of
          any kind. Users are solely responsible for evaluating and verifying all information and
          should consult qualified professionals where appropriate before making business decisions.
        </p>

        <SubHeading>User Responsibility &amp; Assumption of Risk</SubHeading>
        <p>By using the platform, you acknowledge and agree that:</p>
        <ul>
          <li>You are solely responsible for all data you input into Sivo</li>
          <li>You assume full responsibility for any decisions made based on such data</li>
          <li>Any reliance on calculations or outputs generated by the platform is at your own risk</li>
        </ul>

        <SubHeading>Limitation of Liability</SubHeading>
        <p>
          Signage shall not be held liable for any errors, inaccuracies, omissions, or outcomes
          resulting from user-provided data or the use of the platform, including but not limited
          to financial losses, lost profits, or business interruptions.
        </p>

        <SubHeading>Full Disclaimer</SubHeading>
        <p>
          To the fullest extent permitted by law, Signage disclaims all liability arising from
          reliance on user-provided data within the platform.
        </p>

        <SectionHeading>9. System Emails &amp; Communication Disclaimer</SectionHeading>
        <p>
          Sivo, a product of Signage LLC ("Signage," "we," "our," or "us"), may send automated
          emails on behalf of users, including but not limited to quotes, project updates, and order
          communications (collectively, "System Emails").
        </p>

        <SubHeading>Unmonitored Mailboxes</SubHeading>
        <p>
          System Emails may be sent from addresses designated as "no-reply" or unmonitored
          mailboxes. These mailboxes are not intended for direct communication, and Signage does not
          guarantee that any messages sent in response will be received, reviewed, or responded to.
        </p>

        <SubHeading>Required Email Notice</SubHeading>
        <p>System Emails may include the following notice:</p>
        <p>
          <Emphasis>DO NOT REPLY TO THIS EMAIL</Emphasis>
          <br />
          This email was sent from an unmonitored mailbox. To respond, please use the quote review
          link below to leave comments directly on the project, or reply directly to _____ (User's
          Email).
        </p>
        <p>
          <Emphasis>DO NOT REPLY TO THIS EMAIL</Emphasis>
          <br />
          This email was sent from an unmonitored mailbox. Please ensure you are replying directly
          to the sender of this order, not to this email address.
        </p>

        <SubHeading>Proper Communication Channels</SubHeading>
        <p>
          Recipients of System Emails are instructed to communicate directly with the originating
          user through the contact information provided in the email or through designated platform
          features (such as project links or comment portals). Signage is not responsible for
          missed, delayed, or failed communications resulting from replies sent to unmonitored email
          addresses.
        </p>

        <SubHeading>No Responsibility for Third-Party Communications</SubHeading>
        <p>
          Signage acts solely as a transmission platform for System Emails sent on behalf of users.
          We are not a party to, and do not control, any communications, negotiations, agreements,
          or transactions between users and their clients, customers, or suppliers.
        </p>

        <SubHeading>Data Handling &amp; Privacy</SubHeading>
        <p>
          While System Emails are not intended to receive replies, any communications sent to such
          addresses may be processed by our systems for operational, security, or compliance
          purposes. Signage does not actively monitor or manage these communications as a primary
          means of interaction between parties.
        </p>

        <SubHeading>Limitation of Liability</SubHeading>
        <p>
          To the fullest extent permitted by law, Signage shall not be liable for any loss,
          damages, miscommunication, or business impact arising from the use of System Emails,
          including any replies sent to unmonitored email addresses.
        </p>

        <SectionHeading>10. Governing Law</SectionHeading>
        <p>
          This Agreement is governed by and construed in accordance with the laws of the State of
          Texas, United States, without regard to conflict of law principles.
        </p>

        <SectionHeading>11. Entire Agreement</SectionHeading>
        <p>
          This Agreement, together with our Terms of Service and Privacy Policy, constitutes the entire
          agreement between you and Signage regarding use of the Software.
        </p>

        <Link to="/">
          <BackButton variant="primary">Back to Website</BackButton>
        </Link>
      </LegalContainer>
    </LegalSection>
  );
};

export default SoftwareLicenseAgreement;
